import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { useTheme } from 'contexts/ThemeContext';

import { ACTIVATION } from 'utils/rest';

import { Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { Container, Item } from 'components/Layout';

import {
  PENDING_UPSERT_STATUSES,
  SETUP_IN_PROGRESS_STATUSES
} from '../../utils';

import ModalGestion from './ModalGestion';
import ModalParams from './ModalParams';
import ModalParamsMulti from './ModalParamsMulti';

const TabLabel = ({ label, section, selectedSection, onSelect }) => {
  const { themeColors } = useTheme();
  const { designSystem } = themeColors;
  const isSelected = selectedSection === section;

  return (
    <Text
      component="span"
      onClick={() => onSelect(section)}
      fontSize="13.5px"
      fontWeight={600}
      style={{
        cursor: 'pointer',
        paddingBottom: '11px',
        color: isSelected ? designSystem.ink : designSystem.faint,
        borderBottom: isSelected
          ? `2.5px solid ${designSystem.ink}`
          : '2.5px solid transparent'
      }}
    >
      {label}
    </Text>
  );
};

TabLabel.propTypes = {
  label: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  selectedSection: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired
};

const ModalCampaign = props => {
  const { themeColors } = useTheme();
  const { onClose, config, campaign, onValidate, onDelete, datas } = props;
  const [selectedSection, setSelectedSection] = useState('gestion');
  const getActions = () => {};
  const setupPendingTooltip =
    'Merci d’attendre la fin du setup avant de modifier la campagne.';

  const [updatedCampaigns, setUpdatedCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpsertBadge, setShowUpsertBadge] = useState(
    PENDING_UPSERT_STATUSES.includes(campaign.lastUpsertTask?.status) &&
      !SETUP_IN_PROGRESS_STATUSES.includes(campaign.setup?.[0]?.status)
  );

  const setCampaign = async () => {
    const camps = [];
    for (const campId of campaign.campaignConfigIds) {
      const camp = await ACTIVATION.getCampaignConfigById(campId);
      camps.push(camp);
    }
    setUpdatedCampaigns(camps);
    setLoading(false);
  };

  useEffect(() => {
    if (campaign.isCampaignGroup) {
      setCampaign();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Modal
      actions={getActions()}
      {...props}
      onClose={() => {
        onClose();
      }}
      maxWidth={selectedSection === 'gestion' ? 'md' : 'lg'}
    >
      <Container>
        <Item
          flex
          style={{
            width: '100%',
            alignItems: 'center',
            borderBottom: `1px solid ${themeColors.designSystem.line}`,
            marginBottom: '8px'
          }}
        >
          <Item
            flex
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              gap: '6px',
              paddingBottom: '11px'
            }}
          >
            {showUpsertBadge && (
              <>
                <AccessTimeIcon
                  style={{
                    fontSize: '15px',
                    color: themeColors.designSystem.orange
                  }}
                />
                <Text
                  component="span"
                  fontSize="12.5px"
                  fontWeight={700}
                  style={{ color: themeColors.designSystem.orange }}
                >
                  Upsert en attente de setup
                </Text>
              </>
            )}
          </Item>
          <Item
            flex
            style={{
              justifyContent: 'center',
              gap: '28px'
            }}
          >
            <TabLabel
              label="Gestion"
              section="gestion"
              selectedSection={selectedSection}
              onSelect={setSelectedSection}
            />
            {!campaign.isCampaignGroup ? (
              <>
                <TabLabel
                  label="Récapitulatif"
                  section="recap"
                  selectedSection={selectedSection}
                  onSelect={setSelectedSection}
                />
                <TabLabel
                  label="Setup automatique"
                  section="setup"
                  selectedSection={selectedSection}
                  onSelect={setSelectedSection}
                />
              </>
            ) : (
              <TabLabel
                label="Paramétrage"
                section="paramétrageMulti"
                selectedSection={selectedSection}
                onSelect={setSelectedSection}
              />
            )}
          </Item>
          <Item flex style={{ flex: 1 }} />
        </Item>
        {(selectedSection === 'recap' || selectedSection === 'setup') && (
          <ModalParams
            campaign={campaign}
            config={config}
            onValidate={onValidate}
            onClose={onClose}
            setupPendingTooltip={setupPendingTooltip}
            selectedSection={selectedSection}
            onUpsertBadgeChange={setShowUpsertBadge}
          />
        )}
        {selectedSection === 'gestion' && (
          <ModalGestion
            campaign={campaign}
            config={config}
            onValidate={onValidate}
            onDelete={onDelete}
            setupPendingTooltip={setupPendingTooltip}
            datas={{
              importModel: datas.importModel,
              configGenerators: datas.configGenerators,
              configConnexion: datas.configConnexion
            }}
          />
        )}
        {selectedSection === 'paramétrageMulti' && (
          <ModalParamsMulti
            campaign={campaign}
            config={config}
            onValidate={onValidate}
            onClose={onClose}
            setupPendingTooltip={setupPendingTooltip}
            updatedCampaigns={updatedCampaigns}
            loading={loading}
          />
        )}
      </Container>
    </Modal>
  );
};

ModalCampaign.defaultProps = {
  onClose: () => null,
  onValidate: () => {},
  onDelete: () => {}
};
ModalCampaign.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  config: PropTypes.shape().isRequired,
  campaign: PropTypes.shape().isRequired,
  onValidate: PropTypes.func,
  onDelete: PropTypes.func
};

export default ModalCampaign;
